import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormControlLabel, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AddIcon from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCarRounded';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import CallSplitIcon from '@mui/icons-material/CallSplitRounded';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PuffLoader from 'react-spinners/PuffLoader';
import ExpenseAdjustmentsService, {
    EXPENSE_ADJUSTMENTS_TRANSFERRED_EVENT,
    VEHICLE_COST_KIND,
    VEHICLE_COST_SPLIT,
} from '../../../../Services/expenseAdjustments.service';
import { SALARY_SOURCE } from '../../../../Services/salaries.service';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import { vehicleName, vehicleSubtitle } from '../../../AnnualReport/ExpenseAdjustments/adjustmentUtils';
import CodeSourcePicker from '../../../Widgets/CodeSourcePicker/CodeSourcePicker.jsx';
import useCodeSourceOptions from '../../../Widgets/CodeSourcePicker/useCodeSourceOptions';
import AssetModalShell from '../AssetModalShell/AssetModalShell';
import shell from '../AssetModalShell/AssetModalShell.module.scss';
import { xtramInputProps } from '../AssetModalShell/assetModalInputs';
import styles from './VehicleExpensesModal.module.scss';

const emptySource = () => ({ sourceType: SALARY_SOURCE.ACCOUNT_CARDS, codes: [] });
const amount = (value) => beautifyNumber(Math.round(Number(value || 0) * 100) / 100);

const toVehicleDrafts = (overview) => Object.fromEntries((overview?.vehicles || []).map((item) => [item.assetId, {
    variableCosts: item.vehicle?.variableCosts || emptySource(),
    fixedCosts: item.vehicle?.fixedCosts || emptySource(),
    filterByDocumentDate: !!item.vehicle?.filterByDocumentDate,
}]));

const toPoolDrafts = (overview) => (overview?.vehiclePools || []).map((pool) => ({
    ...pool,
    uid: pool.id,
    shares: Object.fromEntries((pool.shares || []).map((s) => [s.assetId, String(s.percentage)])),
}));

/**
 * הוצאות רכבים: every vehicle of the year with its own expense accounts, and the shared expenses (e.g. ריכוז דלק)
 * that are divided between the vehicles equally or by percentages. The annual report's vehicle adjustment reads them.
 */
const VehicleExpensesModal = ({ open, onClose, client, token, year: initialYear, direction }) => {
    const { t } = useTranslation();
    const caseId = client?.caseId;
    const [year, setYear] = useState(initialYear);
    const [overview, setOverview] = useState(null);
    const [vehicleDrafts, setVehicleDrafts] = useState({});
    const [pools, setPools] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const optionsBySource = useCodeSourceOptions({ enabled: open, token, caseId, accountantId: client?.accountantId });
    const inputProps = xtramInputProps(direction);

    useEffect(() => {
        if (open) setYear(initialYear);
    }, [open, initialYear]);

    const apply = (next) => {
        setOverview(next);
        setVehicleDrafts(toVehicleDrafts(next));
        setPools(toPoolDrafts(next));
        setDirty(false);
    };

    const load = useCallback(async () => {
        if (!open || !caseId || !token || !year) return;
        setLoading(true);
        try {
            apply(await ExpenseAdjustmentsService.getOverview({ caseId, year }, token));
        } catch (error) {
            console.error('Loading the vehicle expenses failed:', error);
            toast.error(t('VehicleExpensesLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [open, caseId, token, year, t]);

    useEffect(() => {
        load();
    }, [load]);

    const vehicles = useMemo(() => overview?.vehicles || [], [overview]);
    const vehicleLabel = (item) => vehicleName(item);

    const updateVehicle = (assetId, patch) => {
        setVehicleDrafts((prev) => ({ ...prev, [assetId]: { ...prev[assetId], ...patch } }));
        setDirty(true);
    };

    const updatePool = (uid, patch) => {
        setPools((prev) => prev.map((pool) => (pool.uid === uid ? { ...pool, ...patch } : pool)));
        setDirty(true);
    };

    const addPool = (costKind) => {
        const uid = `new-${Date.now()}`;
        setPools((prev) => [...prev, {
            uid,
            id: null,
            name: costKind === VEHICLE_COST_KIND.VARIABLE ? t('VehicleSharedFuelDefault') : t('VehicleSharedFixedDefault'),
            costKind,
            ...emptySource(),
            filterByDocumentDate: false,
            split: VEHICLE_COST_SPLIT.EQUAL,
            shares: {},
            balance: null,
            allocations: [],
        }]);
        setDirty(true);
    };

    const removePool = (uid) => {
        setPools((prev) => prev.filter((pool) => pool.uid !== uid));
        setDirty(true);
    };

    const sharesTotal = (pool) => Object.values(pool.shares || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);

    const handleSave = async () => {
        const invalid = pools.find((pool) => !(pool.name || '').trim()
            || (pool.split === VEHICLE_COST_SPLIT.PERCENTAGES && sharesTotal(pool) > 100));
        if (invalid) {
            toast.error(t('VehicleSharedInvalid', { name: invalid.name || '—' }));
            return;
        }
        setSaving(true);
        try {
            const next = await ExpenseAdjustmentsService.saveVehicleExpenses({
                caseId,
                year,
                vehicles: Object.entries(vehicleDrafts).map(([assetId, sources]) => ({ assetId, ...sources })),
                pools: pools.map((pool) => ({
                    id: pool.id || null,
                    name: pool.name.trim(),
                    costKind: pool.costKind,
                    sourceType: pool.sourceType,
                    codes: pool.codes || [],
                    filterByDocumentDate: !!pool.filterByDocumentDate,
                    split: pool.split,
                    shares: Object.entries(pool.shares || {})
                        .filter(([, value]) => Number(value) > 0)
                        .map(([assetId, value]) => ({ assetId, percentage: Number(value) })),
                })),
            }, token);
            apply(next);
            window.dispatchEvent(new CustomEvent(EXPENSE_ADJUSTMENTS_TRANSFERRED_EVENT, {
                detail: { caseId, year, source: 'assets' },
            }));
            toast.success(t('VehicleExpensesSaved'));
        } catch (error) {
            console.error('Saving the vehicle expenses failed:', error);
            toast.error(t('VehicleExpensesSaveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const poolShare = (item, kind) => (item.poolShares || [])
        .filter((share) => share.costKind === kind)
        .reduce((sum, share) => sum + Number(share.amount || 0), 0);

    const renderVehicle = (item) => {
        const draft = vehicleDrafts[item.assetId];
        if (!draft) return null;
        const recognizedByUseValue = Math.max(0, Number(item.totalExpenses || 0) - Number(item.annualUseValue || 0));
        return (
            <div key={item.assetId} className={styles.card}>
                <div className={styles.cardHead}>
                    <DirectionsCarIcon />
                    <b>{vehicleLabel(item)}</b>
                    <span className={styles.meta}>{vehicleSubtitle(item)}</span>
                </div>
                <CodeSourcePicker
                    label={t('VehicleVariableCosts')}
                    value={draft.variableCosts}
                    onChange={(next) => updateVehicle(item.assetId, { variableCosts: next })}
                    optionsBySource={optionsBySource}
                />
                <CodeSourcePicker
                    label={t('VehicleFixedCosts')}
                    value={draft.fixedCosts}
                    onChange={(next) => updateVehicle(item.assetId, { fixedCosts: next })}
                    optionsBySource={optionsBySource}
                />
                <FormControlLabel
                    control={(
                        <Checkbox
                            size="small"
                            checked={!!draft.filterByDocumentDate}
                            onChange={(e) => updateVehicle(item.assetId, { filterByDocumentDate: e.target.checked })}
                        />
                    )}
                    label={t('FilterByDocumentDate')}
                />
                <div className={styles.calc}>
                    <span>{t('VehicleVariableShort')}</span>
                    <b>{amount(item.ownVariableCosts)}{poolShare(item, VEHICLE_COST_KIND.VARIABLE) ? ` + ${amount(poolShare(item, VEHICLE_COST_KIND.VARIABLE))}` : ''}</b>
                    <span>{t('VehicleFixedShort')}</span>
                    <b>{amount(item.ownFixedCosts)}{poolShare(item, VEHICLE_COST_KIND.FIXED) ? ` + ${amount(poolShare(item, VEHICLE_COST_KIND.FIXED))}` : ''}</b>
                    <span>{t('VehicleDepreciationShort')}</span>
                    <b>{amount(item.depreciation)}</b>
                    <span>{t('VehicleTotalExpenses')}</span>
                    <b>{amount(item.totalExpenses)}</b>
                    <span>−&nbsp;{t('VehicleYearlyUseValue')}</span>
                    <b>{amount(item.annualUseValue)}</b>
                    <span>=&nbsp;{t('VehicleRecognizedByUseValue')}</span>
                    <b>{amount(recognizedByUseValue)}</b>
                    <span>{t('VehicleAdjustmentAmount')}</span>
                    <b className={styles.adjustment}>{amount(item.adjustment)}</b>
                </div>
            </div>
        );
    };

    const renderPool = (pool) => {
        const total = sharesTotal(pool);
        const allocationOf = (assetId) => (pool.allocations || []).find((a) => a.assetId === assetId)?.amount;
        return (
            <div key={pool.uid} className={styles.card}>
                <div className={styles.poolHead}>
                    <TextField
                        variant="standard"
                        value={pool.name}
                        onChange={(e) => updatePool(pool.uid, { name: e.target.value })}
                        InputProps={inputProps}
                        placeholder={t('VehicleSharedName')}
                        className={styles.poolName}
                    />
                    <TextField
                        select
                        variant="standard"
                        value={pool.costKind}
                        onChange={(e) => updatePool(pool.uid, { costKind: Number(e.target.value) })}
                        InputProps={inputProps}
                        className={styles.poolKind}
                    >
                        <MenuItem value={VEHICLE_COST_KIND.VARIABLE}>{t('VehicleVariableCosts')}</MenuItem>
                        <MenuItem value={VEHICLE_COST_KIND.FIXED}>{t('VehicleFixedCosts')}</MenuItem>
                    </TextField>
                    <button type="button" className={`${shell.dangerBtn} ${shell.small}`} onClick={() => removePool(pool.uid)}>
                        <DeleteIcon />
                        {t('Delete')}
                    </button>
                </div>
                <CodeSourcePicker
                    label={t('VehicleSharedAccounts')}
                    value={{ sourceType: pool.sourceType, codes: pool.codes || [] }}
                    onChange={(next) => updatePool(pool.uid, { sourceType: next.sourceType, codes: next.codes })}
                    optionsBySource={optionsBySource}
                />
                <div className={styles.splitRow}>
                    <span className={styles.splitLabel}>{t('VehicleSharedSplit')}</span>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={pool.split}
                        onChange={(e, next) => next !== null && updatePool(pool.uid, { split: next })}
                    >
                        <ToggleButton value={VEHICLE_COST_SPLIT.EQUAL}>{t('VehicleSplitEqual')}</ToggleButton>
                        <ToggleButton value={VEHICLE_COST_SPLIT.PERCENTAGES}>{t('VehicleSplitPercentages')}</ToggleButton>
                    </ToggleButtonGroup>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                size="small"
                                checked={!!pool.filterByDocumentDate}
                                onChange={(e) => updatePool(pool.uid, { filterByDocumentDate: e.target.checked })}
                            />
                        )}
                        label={t('FilterByDocumentDate')}
                    />
                    {pool.balance != null && !dirty ? (
                        <span className={shell.stat}>{t('VehicleSharedBalance')} <strong>{amount(pool.balance)}</strong></span>
                    ) : null}
                </div>
                <div className={styles.shareGrid}>
                    {vehicles.map((item) => (
                        <div key={item.assetId} className={styles.shareCell}>
                            <span className={styles.shareVehicle}>{vehicleLabel(item)}</span>
                            {pool.split === VEHICLE_COST_SPLIT.PERCENTAGES ? (
                                <TextField
                                    variant="standard"
                                    type="number"
                                    value={pool.shares?.[item.assetId] ?? ''}
                                    onChange={(e) => updatePool(pool.uid, { shares: { ...pool.shares, [item.assetId]: e.target.value } })}
                                    InputProps={{ ...inputProps, endAdornment: '%' }}
                                    inputProps={{ min: 0, max: 100, style: { direction: 'ltr' } }}
                                    className={styles.shareInput}
                                />
                            ) : (
                                <span className={styles.shareEqual}>{vehicles.length ? `${amount(100 / vehicles.length)}%` : ''}</span>
                            )}
                            {!dirty && allocationOf(item.assetId) != null ? (
                                <b className={styles.shareAmount}>{amount(allocationOf(item.assetId))}</b>
                            ) : null}
                        </div>
                    ))}
                </div>
                {pool.split === VEHICLE_COST_SPLIT.PERCENTAGES ? (
                    <span className={`${shell.stat} ${total > 100 ? shell.statWarning : ''}`}>
                        {t('VehicleSharesTotal')} <strong>{amount(total)}%</strong>
                    </span>
                ) : null}
            </div>
        );
    };

    const yearOptions = [initialYear - 2, initialYear - 1, initialYear, initialYear + 1].filter((y) => y > 2000);
    const totals = {
        expenses: vehicles.reduce((sum, v) => sum + Number(v.totalExpenses || 0), 0),
        useValue: vehicles.reduce((sum, v) => sum + Number(v.annualUseValue || 0), 0),
        adjustment: vehicles.reduce((sum, v) => sum + Number(v.adjustment || 0), 0),
    };

    const toolbar = (
        <div className={shell.toolbar}>
            <TextField
                select
                variant="standard"
                value={year}
                onChange={(e) => {
                    if (dirty && !window.confirm(t('VehicleExpensesDiscard'))) return;
                    setYear(Number(e.target.value));
                }}
                InputProps={inputProps}
                sx={{ minWidth: 110 }}
            >
                {yearOptions.map((y) => <MenuItem key={y} value={y}>{t('annualReport.depreciationTransfer.taxYear', { year: y })}</MenuItem>)}
            </TextField>
            <div className={shell.stats}>
                <span className={shell.stat}>{t('VehicleCount')} <strong>{vehicles.length}</strong></span>
                <span className={shell.stat}>{t('VehicleTotalExpenses')} <strong>{amount(totals.expenses)}</strong></span>
                <span className={shell.stat}>{t('VehicleYearlyUseValue')} <strong>{amount(totals.useValue)}</strong></span>
                <span className={`${shell.stat} ${shell.statWarning}`}>{t('VehicleAdjustmentAmount')} <strong>{amount(totals.adjustment)}</strong></span>
            </div>
        </div>
    );

    const footer = (
        <>
            <button type="button" className={shell.secondaryBtn} onClick={onClose}>{t('Close')}</button>
            <button type="button" className={shell.primaryBtn} onClick={handleSave} disabled={saving || !dirty}>
                <SaveIcon />
                {saving ? t('Loading') : t('Save')}
            </button>
        </>
    );

    return (
        <AssetModalShell
            open={open}
            onClose={onClose}
            title={t('VehicleExpenses')}
            subtitle={client?.name || null}
            direction={direction}
            size="xl"
            fullHeight
            toolbar={toolbar}
            footer={footer}
        >
            {loading && !overview ? (
                <div className={shell.loading}><PuffLoader size={50} /></div>
            ) : (
                <>
                    <section className={shell.section}>
                        <h3 className={shell.sectionTitle}>
                            <DirectionsCarIcon sx={{ fontSize: 18 }} />
                            {t('VehicleExpensesPerVehicle')}
                        </h3>
                        {vehicles.length === 0 ? (
                            <div className={shell.emptyState}>{t('annualReport.expenseAdjustments.noVehicles')}</div>
                        ) : (
                            <div className={styles.list}>{vehicles.map(renderVehicle)}</div>
                        )}
                    </section>

                    <section className={shell.section}>
                        <h3 className={shell.sectionTitle}>
                            <CallSplitIcon sx={{ fontSize: 18 }} />
                            {t('VehicleSharedExpenses')}
                        </h3>
                        <p className={shell.muted}>{t('VehicleSharedExpensesHint')}</p>
                        {pools.length > 0 ? <div className={styles.list}>{pools.map(renderPool)}</div> : null}
                        <div className={shell.toolbar} style={{ marginTop: 10 }}>
                            <button type="button" className={`${shell.blueBtn} ${shell.small}`} onClick={() => addPool(VEHICLE_COST_KIND.VARIABLE)}>
                                <AddIcon />
                                {t('VehicleAddSharedVariable')}
                            </button>
                            <button type="button" className={`${shell.secondaryBtn} ${shell.small}`} onClick={() => addPool(VEHICLE_COST_KIND.FIXED)}>
                                <AddIcon />
                                {t('VehicleAddSharedFixed')}
                            </button>
                        </div>
                    </section>
                    {dirty ? <p className={shell.muted}>{t('VehicleExpensesSaveToRecalculate')}</p> : null}
                </>
            )}
        </AssetModalShell>
    );
};

VehicleExpensesModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    client: PropTypes.shape({
        caseId: PropTypes.string,
        accountantId: PropTypes.string,
        name: PropTypes.string,
    }),
    token: PropTypes.string,
    year: PropTypes.number.isRequired,
    direction: PropTypes.oneOf(['rtl', 'ltr']),
};

export default VehicleExpensesModal;
